import { useState } from 'react';
import { Icon } from '../components/Icon';

type Severity = 'info' | 'warn' | 'danger';

type TroubleItem = {
  id: string;
  symptom: string;
  cause: string;
  action: string;
  severity: Severity;
};

const TROUBLESHOOT: TroubleItem[] = [
  {
    id: 'allHot',
    symptom: 'All EGTs raised ~75 °F',
    cause: 'Failed magneto — one plug per cylinder not firing',
    action: 'In-flight mag check carefully; land soon.',
    severity: 'warn',
  },
  {
    id: 'oneHot',
    symptom: 'One EGT raised',
    cause: 'A spark plug on that cylinder stopped firing',
    action: 'In-flight mag check, inspect plug on ground.',
    severity: 'warn',
  },
  {
    id: 'oneHotRop',
    symptom: 'One EGT + CHT raised (ROP)',
    cause: 'Partially restricted fuel injector on that cylinder',
    action: 'Full rich, land and clean injector.',
    severity: 'warn',
  },
  {
    id: 'oneCold',
    symptom: 'One EGT cold',
    cause: 'Injector fully blocked — no combustion',
    action: 'High boost pump briefly; land ASAP.',
    severity: 'danger',
  },
  {
    id: 'runaway',
    symptom: 'One CHT climbing uncontrollably',
    cause: 'Pre-ignition — engine destruction in minutes',
    action: 'Reduce power IMMEDIATELY, enrich, land.',
    severity: 'danger',
  },
  {
    id: 'spread',
    symptom: 'GAMI spread > 1.0 GPH',
    cause: 'Unbalanced injectors — cannot run smooth LOP',
    action: 'Send injectors for calibration.',
    severity: 'info',
  },
];

const SEVERITY_LABEL: Record<Severity, string> = {
  danger: 'Immediate action',
  warn: 'Land soon',
  info: 'Advisory',
};

export function TroubleshootScreen() {
  const [selected, setSelected] = useState<string>('runaway');
  const item = TROUBLESHOOT.find((t) => t.id === selected) ?? TROUBLESHOOT[0];

  return (
    <div className="grid-2" style={{ gridTemplateColumns: '340px 1fr', gap: 20 }}>
      <div className="card" style={{ padding: 8 }}>
        <div className="card-title" style={{ padding: '12px 12px 8px' }}>
          Observed pattern
        </div>
        <div>
          {TROUBLESHOOT.map((t) => (
            <div
              key={t.id}
              onClick={() => setSelected(t.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelected(t.id);
              }}
              style={{
                padding: '12px 12px',
                borderRadius: 12,
                cursor: 'pointer',
                background: selected === t.id ? 'var(--surface-2)' : 'transparent',
                marginBottom: 4,
              }}
            >
              <div className="row" style={{ marginBottom: 4 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background:
                      t.severity === 'danger'
                        ? 'var(--danger)'
                        : t.severity === 'warn'
                          ? 'var(--warn)'
                          : 'var(--ink-3)',
                  }}
                />
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                  {t.symptom}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', paddingLeft: 16 }}>
                {t.cause}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="col gap-lg">
        <div className="card card-lg">
          <span className={`chip ${item.severity}`} style={{ marginBottom: 16 }}>
            <span className="dot" /> {SEVERITY_LABEL[item.severity]}
          </span>
          <h2
            style={{
              margin: '0 0 6px',
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: '-0.015em',
            }}
          >
            {item.symptom}
          </h2>
          <p
            style={{
              fontSize: 15,
              color: 'var(--ink-2)',
              margin: '0 0 20px',
              lineHeight: 1.55,
            }}
          >
            <strong>Likely cause:</strong> {item.cause}
          </p>

          <div className={`callout ${item.severity}`}>
            <Icon name={item.severity === 'danger' ? 'alert' : 'spark'} size={18} />
            <div>
              <div className="callout-title">Action</div>
              <div>{item.action}</div>
            </div>
          </div>
        </div>

        {item.id === 'runaway' && (
          <div className="card card-lg">
            <div className="card-h" style={{ marginBottom: 16 }}>
              Pre-ignition response — in order
            </div>
            <div className="col gap-sm">
              {[
                'Reduce power (throttle back) — lowers peak cylinder pressure fast.',
                'Enrich mixture fully — raises fuel cooling margin.',
                'Open cowl flaps — maximum cooling airflow.',
                'Descend if practical — denser cooling air.',
                'Land at nearest suitable airport. Inspect the cylinder.',
              ].map((t, i) => (
                <div key={i} className="bullet">
                  <div className="idx">{String(i + 1).padStart(2, '0')}</div>
                  <div>{t}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-h" style={{ marginBottom: 10 }}>
            EGT is a diagnostic, not a setpoint
          </div>
          <p
            style={{
              fontSize: 14,
              color: 'var(--ink-2)',
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            Absolute EGT values are near-meaningless — the probe samples pulses, not a
            continuous gas stream. What matters is the <em>pattern across cylinders</em>
            {' '}and <em>changes over time</em>.
          </p>
        </div>
      </div>
    </div>
  );
}
